import React, { useEffect, useState, useCallback } from "react";
import { Table, Spinner, Button, Form, Row, Col, Badge, Alert as BsAlert } from "react-bootstrap";
import SafetySediOperativeService from "../../../services/safety/SafetySediOperativeService";

const EMPTY = {
    id_sede: null,
    denominazione: "",
    tipo_sede: "SEDE_OPERATIVA",
    indirizzo: "",
    cap: "",
    citta: "",
    provincia: "",
    nazione: "Italia",
    referente_nome: "",
    referente_email: "",
    referente_telefono: "",
    note: "",
    attiva: 1,
};

const TIPI_SEDE = [
    { value: "SEDE_LEGALE",    label: "Sede legale" },
    { value: "SEDE_OPERATIVA", label: "Sede operativa" },
    { value: "CANTIERE",       label: "Cantiere" },
    { value: "UFFICIO",        label: "Ufficio" },
    { value: "FILIALE",        label: "Filiale" },
    { value: "MAGAZZINO",      label: "Magazzino" },
    { value: "ALTRO",          label: "Altro" },
];

function tipoLabel(v) {
    const t = TIPI_SEDE.find(o => o.value === v);
    return t ? t.label : (v || "—");
}

export default function SediPanel({ idAzienda }) {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [msg, setMsg] = useState(null);
    const [show, setShow] = useState(false);
    const [form, setForm] = useState(EMPTY);
    const [saving, setSaving] = useState(false);

    const loadItems = useCallback(async () => {
        if (!idAzienda) { setItems([]); return; }
        setLoading(true);
        setError(null);
        try {
            const resp = await SafetySediOperativeService.findByAzienda(idAzienda);
            const data = resp && typeof resp.json === "function" ? await resp.json() : resp;
            setItems(Array.isArray(data) ? data : []);
        } catch (err) {
            const m = err?.message || err?.error?.message || "";
            setError(m.includes("NetworkError") || m.includes("Failed to fetch")
                ? "API non raggiungibile. Deploy backend (SafetySediOperativeController + Service)."
                : (m || "Errore nel caricamento delle sedi."));
        } finally {
            setLoading(false);
        }
    }, [idAzienda]);

    useEffect(() => { loadItems(); }, [loadItems]);

    const openNew = () => { setForm(EMPTY); setError(null); setMsg(null); setShow(true); };
    const openEdit = (s) => { setForm({ ...EMPTY, ...s }); setError(null); setMsg(null); setShow(true); };

    const onField = (e) => {
        const { name, value, type, checked } = e.target;
        setForm(f => ({ ...f, [name]: type === "checkbox" ? (checked ? 1 : 0) : value }));
    };

    const submit = async () => {
        if (!idAzienda) return;
        if (!form.denominazione || form.denominazione.trim() === "") {
            setError("La denominazione della sede è obbligatoria.");
            return;
        }
        setSaving(true);
        setError(null);
        setMsg(null);
        try {
            if (form.id_sede) {
                await SafetySediOperativeService.update(form);
            } else {
                await SafetySediOperativeService.create({ ...form, id_azienda: idAzienda });
            }
            setMsg("Sede salvata.");
            setShow(false);
            setForm(EMPTY);
            await loadItems();
        } catch (err) {
            setError(err?.message || "Errore nel salvataggio.");
        } finally {
            setSaving(false);
        }
    };

    const onDelete = async (s) => {
        if (!s?.id_sede) return;
        if (!window.confirm(`Eliminare la sede "${s.denominazione}"?`)) return;
        try {
            await SafetySediOperativeService.delete(s.id_sede);
            await loadItems();
        } catch (err) {
            setError(err?.message || "Errore nell'eliminazione.");
        }
    };

    if (!idAzienda) {
        return (
            <BsAlert variant="info" className="py-2 mb-0">
                Salva prima i dati dell'azienda per gestire le sedi operative.
            </BsAlert>
        );
    }

    return (
        <div>
            <div className="d-flex justify-content-between align-items-center mb-2">
                <h6 className="mb-0">
                    <i className="bi bi-geo-alt me-2 text-primary" />
                    Sedi operative
                </h6>
                <Button size="sm" variant="primary" onClick={openNew} disabled={show}>
                    <i className="bi bi-plus-lg me-1" />
                    Aggiungi sede
                </Button>
            </div>

            <div className="text-muted small mb-2">
                Gestisci una o più <strong>sedi operative</strong> dell'azienda (cantieri, uffici, filiali, magazzini).
                Le sedi sono poi assegnabili a lavoratori e nomine (es. medico competente per sede).
            </div>

            {error && <BsAlert variant="danger" className="py-2 small">{error}</BsAlert>}
            {msg && <BsAlert variant="success" className="py-2 small" onClose={() => setMsg(null)} dismissible>{msg}</BsAlert>}

            {show && (
                <div className="border rounded p-3 mb-3" style={{ backgroundColor: "#f8f9fa" }}>
                    <div className="fw-semibold mb-2">{form.id_sede ? "Modifica sede" : "Nuova sede operativa"}</div>
                    <Row className="g-2">
                        <Col xs={12} md={6}>
                            <Form.Label className="small mb-0">Denominazione *</Form.Label>
                            <Form.Control size="sm" name="denominazione" value={form.denominazione || ""} onChange={onField} />
                        </Col>
                        <Col xs={12} md={6}>
                            <Form.Label className="small mb-0">Tipo sede</Form.Label>
                            <Form.Select size="sm" name="tipo_sede" value={form.tipo_sede || "SEDE_OPERATIVA"} onChange={onField}>
                                {TIPI_SEDE.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                            </Form.Select>
                        </Col>
                        <Col xs={12} md={8}>
                            <Form.Label className="small mb-0">Indirizzo</Form.Label>
                            <Form.Control size="sm" name="indirizzo" value={form.indirizzo || ""} onChange={onField} />
                        </Col>
                        <Col xs={4} md={2}>
                            <Form.Label className="small mb-0">CAP</Form.Label>
                            <Form.Control size="sm" name="cap" value={form.cap || ""} onChange={onField} />
                        </Col>
                        <Col xs={8} md={2}>
                            <Form.Label className="small mb-0">Provincia</Form.Label>
                            <Form.Control size="sm" name="provincia" value={form.provincia || ""} onChange={onField} maxLength={2} />
                        </Col>
                        <Col xs={8} md={6}>
                            <Form.Label className="small mb-0">Città</Form.Label>
                            <Form.Control size="sm" name="citta" value={form.citta || ""} onChange={onField} />
                        </Col>
                        <Col xs={4} md={6}>
                            <Form.Label className="small mb-0">Nazione</Form.Label>
                            <Form.Control size="sm" name="nazione" value={form.nazione || ""} onChange={onField} />
                        </Col>
                        <Col xs={12} md={4}>
                            <Form.Label className="small mb-0">Referente</Form.Label>
                            <Form.Control size="sm" name="referente_nome" value={form.referente_nome || ""} onChange={onField} />
                        </Col>
                        <Col xs={12} md={4}>
                            <Form.Label className="small mb-0">Email referente</Form.Label>
                            <Form.Control size="sm" name="referente_email" value={form.referente_email || ""} onChange={onField} />
                        </Col>
                        <Col xs={12} md={4}>
                            <Form.Label className="small mb-0">Telefono referente</Form.Label>
                            <Form.Control size="sm" name="referente_telefono" value={form.referente_telefono || ""} onChange={onField} />
                        </Col>
                        <Col xs={12}>
                            <Form.Label className="small mb-0">Note</Form.Label>
                            <Form.Control as="textarea" rows={2} size="sm" name="note" value={form.note || ""} onChange={onField} />
                        </Col>
                        <Col xs={12}>
                            <Form.Check type="checkbox" label="Sede attiva" name="attiva"
                                checked={Number(form.attiva) === 1} onChange={onField} />
                        </Col>
                    </Row>
                    <div className="mt-2 d-flex gap-2">
                        <Button size="sm" variant="primary" onClick={submit} disabled={saving}>
                            {saving ? <Spinner size="sm" animation="border" /> : "Salva sede"}
                        </Button>
                        <Button size="sm" variant="outline-secondary" onClick={() => setShow(false)} disabled={saving}>Annulla</Button>
                    </div>
                </div>
            )}

            {loading ? (
                <div className="text-muted small"><Spinner size="sm" animation="border" className="me-2" />Caricamento…</div>
            ) : items.length === 0 ? (
                <div className="text-muted small">
                    Nessuna sede operativa registrata. Aggiungi le sedi dove operano i lavoratori.
                </div>
            ) : (
                <Table size="sm" bordered hover responsive className="mb-0">
                    <thead className="table-light">
                        <tr>
                            <th>Denominazione</th>
                            <th>Tipo</th>
                            <th>Indirizzo</th>
                            <th>Referente</th>
                            <th>Stato</th>
                            <th className="text-end">Azioni</th>
                        </tr>
                    </thead>
                    <tbody>
                        {items.map(s => (
                            <tr key={s.id_sede}>
                                <td className="fw-semibold">{s.denominazione}</td>
                                <td>{tipoLabel(s.tipo_sede)}</td>
                                <td>
                                    {s.indirizzo || "—"}
                                    {(s.citta || s.provincia) && (
                                        <div className="text-muted small">
                                            {s.cap ? `${s.cap} ` : ""}{s.citta || ""}{s.provincia ? ` (${s.provincia})` : ""}
                                        </div>
                                    )}
                                </td>
                                <td>{s.referente_nome || <span className="text-muted">—</span>}</td>
                                <td>
                                    {Number(s.attiva) === 1
                                        ? <Badge bg="success">Attiva</Badge>
                                        : <Badge bg="secondary">Disattivata</Badge>}
                                </td>
                                <td className="text-end">
                                    <Button size="sm" variant="outline-primary" className="me-1" onClick={() => openEdit(s)}>
                                        <i className="bi bi-pencil" />
                                    </Button>
                                    <Button size="sm" variant="outline-danger" onClick={() => onDelete(s)}>
                                        <i className="bi bi-trash" />
                                    </Button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
            )}
        </div>
    );
}
